import { defineStore } from 'pinia';
import type { FetchError } from 'ofetch';
import { useUiStore } from './ui';
import gql from '@/gql/cart';
import { FavoriteItemsPaged, FavoriteList, FavoriteListsPaged } from '~/types/list';

export const useListStore = defineStore('list', () => {
  const favoriteListArray = ref<FavoriteList[]>([]);

  type ListsResponse = {
    error: FetchError;
    data: {
      data: {
        favoriteLists: FavoriteListsPaged;
        errors: string[];
      };
    };
  };

  const fetchLists = async (
    limit?: number,
    offset?: number,
    sort?: string,
    userId?: string,
    access?: string,
    fromUpdatedAt?: string,
  ): Promise<{ data?: FavoriteListsPaged; error?: unknown }> => {
    const { $apiGateway, $sentry, $i18n } = useNuxtApp();
    const { showMessage } = useUiStore();

    try {
      const { data, error } = await $apiGateway.post<ListsResponse>('/graphql', {
        query: gql.getFavoriteLists,
        variables: {
          sort,
          access,
          userId,
          fromUpdatedAt,
          pagination: {
            limit,
            offset,
          },
        },
      });

      if (error) throw error;
      const { favoriteLists, errors } = data.data;

      if (errors?.length) {
        throw new Error(errors[0]);
      }

      favoriteListArray.value = favoriteLists.data;
      return { data: favoriteLists };
    } catch (error) {
      $sentry.captureException(error);
      showMessage({ text: $i18n.t('errors.try_again_later_or_contact') });
      return { error };
    }
  };

  const fetchFavouriteListsById = async (userId: string): Promise<{ data?: FavoriteListsPaged; error?: unknown }> => {
    const { $apiGateway, $sentry, $i18n } = useNuxtApp();
    const { showMessage } = useUiStore();

    try {
      const { data, error } = await $apiGateway.post<ListsResponse>('/graphql', {
        query: gql.getFavoriteLists,
        variables: {
          userId,
        },
      });

      if (error) throw error;
      const { favoriteLists, errors } = data.data;

      if (errors?.length) {
        throw new Error(errors[0]);
      }

      return { data: favoriteLists };
    } catch (error) {
      $sentry.captureException(error);
      showMessage({ text: $i18n.t('errors.try_again_later_or_contact') });
      return { error };
    }
  };

  const fetchListItems = async (
    favoriteId: number,
    offset: number,
    limit: number,
  ): Promise<{ data?: FavoriteItemsPaged; error?: boolean }> => {
    const { $cartService, $sentry } = useNuxtApp();
    const { data, error } = await $cartService.getFavoriteItems(favoriteId, offset, limit);
    if (error) {
      $sentry.captureException(error);
      return { error: true };
    }
    return { data };
  };

  const fetchList = async (favoriteId: number): Promise<{ data?: FavoriteList; error?: FetchError }> => {
    const { $cartService, $sentry } = useNuxtApp();
    const { data, error } = await $cartService.getFavorite(favoriteId);
    if (error || !data) {
      $sentry.captureException(error);
      return { error };
    }
    const list = { ...data, totalRows: data.favoriteItems.length };
    return { data: list };
  };

  const deleteFromList = async ({ favoriteId, itemIds }: { favoriteId: number; itemIds: number[] }): Promise<void> => {
    const { $cartService, $sentry } = useNuxtApp();
    const { error: deleteError } = await $cartService.deleteFavoriteItems({ favoriteId, itemIds });
    if (deleteError) $sentry.captureException(deleteError);
  };

  const updateListItem = async ({ favoriteId, item }: { favoriteId: number; item: any }): Promise<void> => {
    const { $cartService, $sentry } = useNuxtApp();
    const { error } = await $cartService.updateFavoriteItem({ favoriteId, item });
    if (error) $sentry.captureException(error);
  };

  return {
    favoriteListArray,
    fetchList,
    fetchLists,
    fetchListItems,
    fetchFavouriteListsById,
    deleteFromList,
    updateListItem,
  };
});
